/**
 * SegmentNotesBlock — Freitext-Notizen am Kundensegment (#32, PV-014).
 *
 * Gleichrangige Section unten in der Übergabe-Ansicht (gleiches Header-Muster wie
 * „Eingehend"/„Ausgehend", nicht als eingerückte Card). Notizen sind ein projektloses
 * Objekt am Segment; Lebenszyklus offen/fertig/gelöscht (soft). @-Mentions wie in
 * Kommentaren, aber ohne Mail. Screenshots lassen sich per Strg+V direkt einfügen
 * (werden hochgeladen und inline angezeigt). Das Worklist-Flag wird PRO Notiz gesetzt.
 *
 * Reader/Nicht-Schreibberechtigte sehen die Notizen; Eingabe + Buttons sind deaktiviert.
 * Da MentionInput kein disabled-Prop hat, rendert der gesperrte Zustand ein inertes
 * textarea-Surrogat (Muster intake-notes-block.tsx) statt des Live-Editors.
 */
import { canWriteSegmentNotes } from '@projektverwaltung/shared';
import type { Segment } from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useReaderMode } from '../../lib/reader.js';
import { useSession } from '../../lib/session.js';
import { MentionInput } from '../mention-input.js';
import { useToast } from '../toast.js';
import { renderNoteBody } from './note-body.js';

const OPEN_STORAGE_KEY = 'pv:handover-notes-open';

export function SegmentNotesBlock({ segment }: { segment: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useSession();
  const isReader = useReaderMode();

  // Default OFFEN (der Bereich ist der Zweck der Seite) — persistiert in sessionStorage.
  const [open, setOpen] = useState<boolean>(
    () => window.sessionStorage.getItem(OPEN_STORAGE_KEY) !== 'closed',
  );
  const [showDone, setShowDone] = useState(false);
  const [draft, setDraft] = useState('');

  const canWrite = !isReader && user != null && canWriteSegmentNotes(user, segment as Segment);

  const { data } = useQuery({
    queryKey: ['v2-segment-notes', segment, showDone],
    queryFn: () => api.listSegmentNotes(segment, { includeDone: showDone }),
    enabled: segment !== '',
  });
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const usersById = useMemo(() => {
    const map = new Map<string, { user_id: string; name: string; email: string }>();
    for (const u of usersQuery.data?.items ?? []) map.set(u.user_id, u);
    return map;
  }, [usersQuery.data]);

  // Alle Sichten dieses Segments invalidieren (showDone-Varianten) + die Worklist.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['v2-segment-notes', segment] });
    void qc.invalidateQueries({ queryKey: ['v2-kfml-worklist'] });
  };

  const create = useMutation({
    mutationFn: () => api.createSegmentNote({ segment, body: draft }),
    onSuccess: () => {
      setDraft('');
      invalidate();
    },
    onError: (err) =>
      toast.error(
        t('v2.handover.notes_create_failed', 'Notiz konnte nicht gespeichert werden'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const patch = useMutation({
    mutationFn: (v: { id: string; done?: boolean; kfml_worklist?: boolean }) => {
      // exactOptionalPropertyTypes: nur gesetzte Felder weiterreichen, kein `key: undefined`.
      const input: { done?: boolean; kfml_worklist?: boolean } = {};
      if (v.done !== undefined) input.done = v.done;
      if (v.kfml_worklist !== undefined) input.kfml_worklist = v.kfml_worklist;
      return api.patchSegmentNote(v.id, input);
    },
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        t('v2.handover.notes_update_failed', 'Notiz konnte nicht geändert werden'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.deleteSegmentNote(id),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        t('v2.handover.notes_delete_failed', 'Notiz konnte nicht gelöscht werden'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  // Bild-Upload für den Editor: gibt die browser-ladbare URL zurück (oder wirft → Toast).
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const { url } = await api.uploadSegmentNoteImage(segment, file);
      return url;
    } catch (err) {
      toast.error(
        t('v2.handover.notes_image_failed', 'Bild konnte nicht hochgeladen werden'),
        err instanceof Error ? err.message : 'Fehler',
      );
      throw err;
    }
  };

  if (segment === '') return null;

  const toggleOpen = () => {
    setOpen((v) => {
      window.sessionStorage.setItem(OPEN_STORAGE_KEY, v ? 'closed' : 'open');
      return !v;
    });
  };

  const notes = data?.items ?? [];
  const readerTooltip = !canWrite ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined;
  const fmt = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );

  return (
    <section className="space-y-3 animate-fade-up-d1">
      {/* Gleiches Header-Muster wie die Section-Komponente (Eingehend/Ausgehend). */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="flex items-baseline gap-2 w-full text-left"
      >
        <span
          className="text-xs"
          style={{
            color: 'var(--ink-3)',
            transform: open ? 'none' : 'rotate(-90deg)',
            transition: 'transform 120ms',
            alignSelf: 'center',
          }}
        >
          ▾
        </span>
        <h2 className="text-base font-semibold" style={{ color: 'var(--ink)' }}>
          {t('v2.handover.notes_title', 'Notizen')}
        </h2>
        <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
          {t('v2.handover.notes_segment_hint', 'Segment {{segment}}', { segment })}
        </span>
        <span
          className="text-[11px]"
          style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
        >
          {notes.length}
        </span>
      </button>

      {open && (
        <div className="space-y-3">
          <p className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
            {t(
              'v2.handover.notes_visibility_hint',
              'Für alle im Haus lesbar. Keine vertraulichen Themen. Erwähnungen lösen keine E-Mail aus.',
            )}
          </p>

          <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-2)' }}>
            <input
              type="checkbox"
              checked={showDone}
              onChange={(e) => setShowDone(e.target.checked)}
            />
            {t('v2.handover.notes_show_done', 'Fertige anzeigen')}
          </label>

          <div className="flex flex-col gap-2">
            {notes.length === 0 && (
              <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                {t('v2.handover.notes_empty', 'Noch keine Notizen für dieses Segment.')}
              </p>
            )}
            {notes.map((n) => {
              const author = n.created_by ? usersById.get(n.created_by) : undefined;
              const isDone = n.done_at != null;
              return (
                <div
                  key={n.segment_note_id}
                  className="rounded-md px-3 py-2"
                  style={{ background: 'var(--surface-2)', color: 'var(--ink)' }}
                >
                  <div
                    className="flex items-center justify-between mb-1 text-[11px]"
                    style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
                  >
                    <span style={{ color: 'var(--ink-2)' }}>
                      {author?.name ?? t('v2.detail.audit_author_unknown', 'unbekannt')}
                    </span>
                    <span>{fmt(n.created_at)}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap" style={{ color: 'var(--ink)' }}>
                    {renderNoteBody(n.body, usersById as never)}
                  </div>
                  {isDone && n.done_at && (
                    <div className="text-[11px] mt-1" style={{ color: 'var(--ink-3)' }}>
                      {t('v2.handover.notes_done_marker', 'fertig · {{date}}', {
                        date: fmt(n.done_at),
                      })}
                    </div>
                  )}
                  {canWrite && (
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <button
                        type="button"
                        onClick={() => patch.mutate({ id: n.segment_note_id, done: !isDone })}
                        style={{ color: 'var(--primary)' }}
                      >
                        {isDone
                          ? t('v2.handover.notes_reopen', 'wieder öffnen')
                          : t('v2.handover.notes_done', 'fertig')}
                      </button>
                      {/* Worklist-Flag PRO Notiz (nicht global beim Anlegen). */}
                      <label
                        className="flex items-center gap-1.5 cursor-pointer"
                        style={{ color: 'var(--ink-2)' }}
                      >
                        <input
                          type="checkbox"
                          checked={n.kfml_worklist}
                          onChange={() =>
                            patch.mutate({
                              id: n.segment_note_id,
                              kfml_worklist: !n.kfml_worklist,
                            })
                          }
                        />
                        {t('v2.handover.notes_flag_on_create', 'auf die Worklist')}
                      </label>
                      <button
                        type="button"
                        onClick={() => del.mutate(n.segment_note_id)}
                        style={{ color: 'var(--danger)' }}
                      >
                        {t('v2.handover.notes_delete', 'löschen')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Eingabe: Live-Editor nur mit Schreibrecht, sonst inertes Surrogat + Tooltip. */}
          <div className="flex flex-col gap-1">
            {canWrite ? (
              <>
                <MentionInput
                  value={draft}
                  onChange={(text: string) => setDraft(text)}
                  placeholder={t('v2.handover.notes_placeholder', 'Neue Notiz… (@ für Erwähnung)')}
                  rows={2}
                  imageUpload={handleImageUpload}
                />
                <p className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
                  {t(
                    'v2.handover.notes_image_hint',
                    'Screenshot mit Strg+V einfügen — wird hochgeladen und in der Notiz angezeigt.',
                  )}
                </p>
              </>
            ) : (
              <textarea
                disabled
                title={readerTooltip}
                placeholder={t('v2.handover.notes_placeholder', 'Neue Notiz… (@ für Erwähnung)')}
                rows={2}
                className="w-full text-sm px-3 py-2 rounded-lg outline-none"
                style={{
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  border: '1px solid var(--line-soft)',
                  opacity: 0.6,
                  cursor: 'not-allowed',
                }}
              />
            )}
            <div className="flex items-center justify-end">
              <button
                type="button"
                disabled={!canWrite || !draft.trim() || create.isPending}
                onClick={() => create.mutate()}
                title={readerTooltip}
                className="text-xs px-3 py-1 rounded"
                style={{
                  background: 'var(--primary)',
                  color: 'var(--on-primary)',
                  border: 'none',
                  opacity: canWrite && draft.trim() && !create.isPending ? 1 : 0.5,
                  cursor: canWrite && draft.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {t('v2.handover.notes_submit', 'Notiz hinzufügen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
